import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import LinkButton from 'flarum/common/components/LinkButton';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';

/*
 * 🚨 Taken from the running garrison bundle, not imported.
 *
 * These two packages build separately, and `command`/`awaitCommand` reach a
 * store that must be the SAME instance as the free half is polling with — a
 * private copy would run a second poll loop against the same endpoint on
 * every paying forum. See garrison/js/src/forum/api.js.
 *
 * 🚨 Looked up when CALLED, never destructured at module scope. These are two
 * separate bundles and nothing orders them: destructuring at the top would
 * throw on `undefined` whenever pro's file happened to evaluate first, and a
 * throw at module scope takes out every extension initialiser after it, so the
 * symptom would land somewhere with no connection to Garrison at all.
 */
const duration = function () {
  return globalThis.Garrison.duration(...arguments);
};

/**
 * Somebody's characters and what they have played, on their profile.
 *
 * 🚨 Fetched when the profile opens, NOT carried on the user resource.
 *
 * Hanging playtime off Flarum's user serializer is the obvious move and the
 * expensive one: every user in every payload would carry it, and a discussion
 * page serializes dozens — a query per rendered item, which is the shape that
 * once exhausted a database connection cap and took a whole forum down. A
 * profile is opened one at a time.
 */
/*
 * 🚨 One fetch per member per page, not one per card.
 *
 * This block also sits in the hover card a post's author name opens, and that
 * card is mounted afresh on every hover. Fetching in oninit meant a request
 * for every hover of every author on a thread — the same member's playtime
 * fetched again each time. Answers (and the in-flight promise) are kept here,
 * by user id, for a few minutes.
 */
const TTL_MS = 5 * 60 * 1000;
const cache = new Map(); // userId -> { at, rows, failed, promise }

function load(userId) {
  const hit = cache.get(userId);
  if (hit && (hit.promise || Date.now() - hit.at < TTL_MS)) return hit;
  const entry = {
    at: Date.now(),
    rows: null,
    failed: false,
    promise: null
  };
  entry.promise = app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + `/garrison/users/${userId}/playtime`
  }).then(res => {
    entry.rows = res && res.data || [];
  }).catch(() => {
    // 🚨 Silent. This is one optional block on somebody else's profile —
    // a red error where a game character would be tells the reader nothing
    // they can act on and makes the whole page look broken.
    entry.failed = true;
  }).then(() => {
    entry.at = Date.now();
    entry.promise = null;
    m.redraw();
  });
  cache.set(userId, entry);
  return entry;
}
export default class ProfilePlaytime extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.entry = load(this.attrs.user.id());
  }
  get rows() {
    return this.entry.rows;
  }
  get failed() {
    return this.entry.failed;
  }
  view() {
    if (this.failed) return null;
    if (this.rows === null) {
      return m("div", {
        className: "GarrisonProfile"
      }, m(LoadingIndicator, {
        display: "inline",
        size: "small"
      }));
    }

    // Nothing linked is the common case for most members, and it gets nothing
    // rather than an empty heading.
    if (this.rows.length === 0) return null;
    return m("div", {
      className: "GarrisonProfile"
    }, m("h3", {
      className: "GarrisonProfile-title"
    }, app.translator.trans('ernestdefoe-garrison.forum.profile.title')), m("ul", {
      className: "GarrisonProfile-list"
    }, this.rows.map(r => m("li", {
      className: "GarrisonProfile-row",
      key: r.serverId
    }, m("span", {
      className: "GarrisonProfile-player"
    }, r.player), m("span", {
      className: "GarrisonProfile-server"
    }, LinkButton.component({
      href: app.route('garrison.server', {
        id: r.serverId
      }),
      className: 'Button Button--link'
    }, r.serverName)), r.seconds >= 60 ? m("span", {
      className: "GarrisonProfile-time"
    }, duration(r.seconds)) : null))));
  }
}
flarum.reg.add('ernestdefoe-garrison-pro', 'forum/components/ProfilePlaytime', ProfilePlaytime);